/**
 * useQuery hook for a single publication's metadata.
 *
 * Replaces `publicationViewEffects.getPublicationById` (compose-app). Mirrors
 * the same `getPublicationById` Hasura document via `postQuery`.
 *
 * Auth note (COMPOSE_RETIREMENT_GUIDELINES §1): Publication View is reachable
 * by non-admin users (publication managers). The stories-service Hasura proxy
 * maps non-admin Auth0 tokens to a role lacking many perms, so this hook uses
 * `postQuery` (cookie/session auth) — NOT `useGraphQLQuery` — matching the
 * retired effect's transport exactly.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 * The retired effect navigated to the publication list on error; that
 * navigation is a component concern and stays at the call site.
 */

import { useQuery } from '@tanstack/react-query';
import { getPublicationById } from '@opengov/stories-shared/graphQL';
import { IPublication } from '@opengov/stories-shared/types';

import { postQuery } from '../../../../effects/publications/postQuery';
import { useToast } from '../../../Toast/context/useToast.context';
import { publicationViewQueryKeys, PublicationIdKeyParams } from './publicationViewQueryKeys';

export type UsePublicationByIdParams = PublicationIdKeyParams | null;

export function usePublicationById(params: UsePublicationByIdParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<IPublication, Error>({
    queryKey: params
      ? publicationViewQueryKeys.publication(params)
      : ([...publicationViewQueryKeys.all, 'publication', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const data = await postQuery(getPublicationById, { id: params.publicationId });
      return data.resource as IPublication;
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
